/**
 * public-question-flow — what a CUSTOMER page does with the settings bag the public
 * read now serves (contract PublicBookingQuestion, widened 2026-10-07). Pure helpers,
 * no rendering, so the walk-in desk and the All journeys booking pages share one
 * set of rules with each other and with question-runtime (Event Day, group preview):
 *   · flowVisible  — show-if (each condition independent) + jump rules
 *   · shownOptions — author order, or A–Z when asked
 *   · isMultiChoice — checks, multi, or a dropdown with several selections
 *   · startingAnswer — prefilled text / a toggle's starting state
 *   · ruleError    — the server's own answer check (findQuestionAnswersError)
 * Data: unbound (helpers; callers own their ops).
 */
import { findQuestionAnswersError } from "@v0y4ge/api-contracts";
import { visibleItems } from "./question-runtime";
import type { Question, GroupItem } from "./useQuestions";

type Row = { id: string; type: string; options?: { id: string; label: string; allowsText?: boolean }[]; rules?: Record<string, string>; branches?: GroupItem["branches"]; showIf?: GroupItem["showIf"]; [k: string]: unknown };

export const otherAnswerKey = (qid: string) => `${qid}::other`;

/** The served questions a respondent currently sees, given their answers so far. An untouched
    question counts as its starting answer, the value the page shows and the submit sends, so
    show-if and jump rules evaluate what the visitor sees. */
export function flowVisible<T extends Row>(rows: T[], answers: Record<string, unknown>): T[] {
  if (!rows.some((r) => (r.rules && Object.keys(r.rules).length) || r.branches?.length || r.showIf?.length)) return rows;
  const items: GroupItem[] = rows.map((r) => ({ qid: r.id, rules: r.rules, branches: r.branches, showIf: r.showIf }));
  const byId = Object.fromEntries(rows.map((r) => [r.id, r as unknown as Question]));
  const keep = new Set(visibleItems(items, byId, normalise(rows, withStarting(rows, answers))).map((it) => it.qid));
  return rows.filter((r) => keep.has(r.id));
}

function withStarting(rows: Row[], answers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...answers };
  for (const r of rows) if (out[r.id] === undefined) { const v = startingAnswer(r as never); if (v !== undefined) out[r.id] = v; }
  return out;
}

/** Page state holds choice answers as an id or id array and toggles as booleans; the runtime wants id arrays. */
function normalise(rows: Row[], answers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...answers };
  for (const r of rows) {
    const v = answers[r.id];
    if (["radio", "select", "checks", "multi"].includes(r.type) && v != null && !Array.isArray(v)) out[r.id] = String(v) ? [String(v)] : [];
  }
  return out;
}

export function shownOptions<T extends { label: string }>(q: { select?: { sortAlphabetically?: boolean } | null; options?: T[] }): T[] {
  const opts = q.options || [];
  return q.select?.sortAlphabetically ? [...opts].sort((a, b) => a.label.localeCompare(b.label)) : opts;
}

export const isMultiChoice = (q: { type: string; select?: { allowMultiple?: boolean } | null }) =>
  q.type === "checks" || q.type === "multi" || (q.type === "select" && !!q.select?.allowMultiple);

export function otherOption(q: { options?: { id: string; allowsText?: boolean }[] }) {
  return (q.options || []).find((o) => o.allowsText) || null;
}

/** The answer a field opens with when the customer hasn't touched it. */
export function startingAnswer(q: { type: string; defaultValue?: string; yesno?: { startsAs?: string } | null }): unknown {
  if (q.type === "yesno") return q.yesno?.startsAs === "yes" ? true : q.yesno?.startsAs === "no" ? false : undefined;
  if ((q.type === "short" || q.type === "long") && q.defaultValue) return q.defaultValue;
  return undefined;
}

export const isReadOnly = (q: { type: string; textRules?: { readOnly?: boolean } | null; yesno?: { readOnly?: boolean } | null }) =>
  !!(q.textRules?.readOnly || (q.type === "yesno" && q.yesno?.readOnly));

/** The server's own message for this answer, or null. `value` must already be in the submitted shape. */
export function ruleError(q: Row & { label: string; required?: boolean }, value: unknown, otherText?: string): string | null {
  if (value === undefined) return null;
  try {
    const dto = { status: "approved", options: [], help: "", updatedAt: "", createdAt: "", ...q, required: false } as never;
    const res = findQuestionAnswersError([dto], [{ questionId: q.id, value, ...(otherText !== undefined ? { otherText } : {}) }]);
    return res ? res.message : null;
  } catch { return null; }
}
